import { CalendarRange } from "lucide-react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";

import { CAMPAIGN_TYPE_LABELS } from "@/@types/gamification";
import { formatDateString } from "@/utils/date-string";

import { COMPETING_CAMPAIGNS_MAX_CHECKED } from "../constants/competing-campaigns";
import { COMPETING_CAMPAIGNS_COPY } from "../constants/messages";
import type {
  CampaignRankPosition,
  CompetingCampaign,
} from "../utils/competing-campaigns";
import { STATUS_COLORS, STATUS_LABELS } from "../utils/status";
import { PriorityBadge } from "./priority-badge";
import {
  CompetingPanelState,
  type UseCompetingCampaignsResult,
} from "./use-competing-campaigns";

const SKELETON_ROWS = 3;

function CampaignRow({ campaign }: { campaign: CompetingCampaign }): JSX.Element {
  return (
    <li className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="break-words text-sm font-medium">{campaign.name}</p>
        <p className="text-xs text-muted-foreground">
          {CAMPAIGN_TYPE_LABELS[campaign.mechanic]} ·{" "}
          {formatDateString(campaign.startDate)} a{" "}
          {formatDateString(campaign.endDate)}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={STATUS_COLORS[campaign.status]}>
          {STATUS_LABELS[campaign.status]}
        </Badge>
        <PriorityBadge value={campaign.priority} />
      </div>
    </li>
  );
}

interface CampaignSectionProps {
  headingId: string;
  title: string;
  help: string;
  campaigns: CompetingCampaign[];
  position?: CampaignRankPosition;
}

function CampaignSection({
  headingId,
  title,
  help,
  campaigns,
  position,
}: CampaignSectionProps): JSX.Element | null {
  if (campaigns.length === 0) return null;
  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <div>
        <h3 id={headingId} className="text-sm font-semibold">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
      {position && (
        <p className="text-sm font-medium">
          {COMPETING_CAMPAIGNS_COPY.position(position.rank, position.total)}
        </p>
      )}
      <ul className="space-y-2">
        {campaigns.map((campaign) => (
          <CampaignRow key={campaign.id} campaign={campaign} />
        ))}
      </ul>
    </section>
  );
}

const Message = ({ text }: { text: string }): JSX.Element => (
  <p className="flex items-center gap-2 text-sm text-muted-foreground">
    <CalendarRange aria-hidden="true" className="h-4 w-4 shrink-0" />
    {text}
  </p>
);

const TruncatedNotice = ({ truncated }: { truncated: boolean }): JSX.Element | null => {
  if (!truncated) return null;
  return (
    <p role="note" className="text-xs text-amber-700">
      {COMPETING_CAMPAIGNS_COPY.truncated(COMPETING_CAMPAIGNS_MAX_CHECKED)}
    </p>
  );
};

type ViewProps = UseCompetingCampaignsResult & { group: string };

const VIEWS: Record<CompetingPanelState, (props: ViewProps) => JSX.Element> = {
  [CompetingPanelState.MISSING_PERIOD]: () => (
    <Message text={COMPETING_CAMPAIGNS_COPY.missingPeriod} />
  ),
  [CompetingPanelState.LOADING]: () => (
    <div role="status" aria-label={COMPETING_CAMPAIGNS_COPY.loading} className="space-y-2">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
  ),
  [CompetingPanelState.ERROR]: ({ retry }) => (
    <div role="alert" className="flex flex-wrap items-center gap-3">
      <p className="text-sm text-red-600">{COMPETING_CAMPAIGNS_COPY.error}</p>
      <Button type="button" variant="outline" size="sm" onClick={retry}>
        {COMPETING_CAMPAIGNS_COPY.retry}
      </Button>
    </div>
  ),
  [CompetingPanelState.EMPTY]: ({ truncated }) => (
    <div className="space-y-2">
      <Message
        text={
          truncated
            ? COMPETING_CAMPAIGNS_COPY.emptyPartial
            : COMPETING_CAMPAIGNS_COPY.empty
        }
      />
      <TruncatedNotice truncated={truncated} />
    </div>
  ),
  [CompetingPanelState.READY]: ({ result, truncated, group }) => (
    <div className="space-y-6">
      <TruncatedNotice truncated={truncated} />
      <CampaignSection
        headingId="competing-same-customer"
        title={COMPETING_CAMPAIGNS_COPY.sameCustomerTitle}
        help={COMPETING_CAMPAIGNS_COPY.sameCustomerHelp(group)}
        campaigns={result?.sameCustomer ?? []}
      />
      <CampaignSection
        headingId="competing-same-game"
        title={COMPETING_CAMPAIGNS_COPY.sameGameTitle}
        help={COMPETING_CAMPAIGNS_COPY.sameGameHelp}
        campaigns={result?.sameGame ?? []}
        position={result?.position}
      />
    </div>
  ),
};

interface CompetingCampaignsPanelProps {
  view: UseCompetingCampaignsResult;
  group: string;
}

export function CompetingCampaignsPanel({
  view,
  group,
}: CompetingCampaignsPanelProps): JSX.Element {
  return (
    <div aria-live="polite" aria-busy={view.state === CompetingPanelState.LOADING}>
      {VIEWS[view.state]({ ...view, group })}
    </div>
  );
}
